"use client";

import React, { useEffect, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useRouter } from "next/navigation";
import Image from "next/image";
import RevealElement from "../revealElement";
import GoogleCaptchaWrapper from "../googleCaptchaWrapper";
import ProgressSteps from "../creator/ProgressSteps";
import StepAbout from "../creator/steps/StepAbout";
import StepSocials from "../creator/steps/StepSocials";
import StepContent from "../creator/steps/StepContent";
import StepPreferences from "../creator/steps/StepPreferences";
import StepJoin from "../creator/steps/StepJoin";
import { EMPTY_APPLICATION } from "@/types/creatorNetwork";
import type { CreatorApplication } from "@/types/creatorNetwork";
import { STEP_VALIDATORS, hasErrors, type FieldErrors } from "@/utils/creatorValidation";
import { postRequest } from "@/utils/postData";
import { getStrapiURL } from "@/utils/api-helpers";
import { googleRecaptcha } from "@/utils/googleRecaptcha";
import {
  getThankYouUrl,
  pushEnhancedConversionsData,
} from "@/utils/gtmEnhancedConversions";

import NotifiactionIcon from "@/app/assets/notification.svg";

const STEP_LABELS = ["About you", "Your socials", "What you create", "Preferences", "Join"];

function resolveOther(value: string, otherValue: string) {
  return value === "others" ? otherValue.trim() : value;
}

function resolveOthersInList(values: string[], otherValue: string) {
  return values.map((v) => resolveOther(v, otherValue));
}

function mapToKolSubmission(formData: CreatorApplication) {
  return {
    name: formData.fullName,
    mobileNumber: formData.mobile,
    email: formData.email,
    state: formData.stateRegion,
    city: formData.city,
    ageRange: formData.ageRange,
    gender: formData.gender,
    occupation: resolveOther(formData.occupation, formData.occupationOther),
    language: resolveOthersInList(formData.languages, formData.languagesOther),
    platform: formData.socialProfiles.map((p) => p.platform),
    username: formData.socialProfiles.map((p) => p.handle).join(" | "),
    followers: formData.socialProfiles.map((p) => p.followerRange),
    contentLink: formData.socialProfiles.flatMap((p) => p.contentLinks),
    contentInterest: resolveOthersInList(formData.interests, formData.interestsOther),
    contentStyle: formData.styles,
    isComfortableOnCamera: formData.showFace,
    isUGConly: formData.ugcOnly,
    campaignTypes: formData.campaignTypes,
    compensationType: formData.compensation,
    isAvailableInPerson: formData.attendEvents,
    availableAreas: resolveOthersInList(formData.areas, formData.areasOther),
    introduction: formData.whyJoin,
    howDidYouHearAboutUs: resolveOther(
      formData.howDidYouHearAboutUs,
      formData.howDidYouHearAboutUsOther
    ),
    referralCode: formData.referralCode,
  };
}

type KolFormData = {
  eyebrow: string;
  heading: string;
  desc: string;
};

type KolFormProps = {
  data?: Partial<KolFormData>;
};

const DEFAULT_DATA: KolFormData = {
  eyebrow: "Join The Network",
  heading: "Ready to Create & Earn?",
  desc: "Your details go straight into Kingdom Digital’s Creator Network — you will only be contacted for briefs that actually match you. ",
};

export default function KolForm(props: KolFormProps) {
  return (
    <GoogleCaptchaWrapper>
      <KolFormInner {...props} />
    </GoogleCaptchaWrapper>
  );
}

function KolFormInner({ data }: KolFormProps) {
  const content: KolFormData = { ...DEFAULT_DATA, ...data };
  const router = useRouter();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<CreatorApplication>({
    ...EMPTY_APPLICATION,
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formSubmitError, setFormSubmitError] = useState(false);

  const totalSteps = STEP_LABELS.length;

  const update = (patch: Partial<CreatorApplication>) => {
    const next = { ...formData, ...patch };
    setFormData(next);

    if (hasErrors(errors)) {
      const validator = STEP_VALIDATORS[currentStep - 1];
      setErrors(validator(next));
    }
  };

  const validateCurrentStep = () => {
    const validator = STEP_VALIDATORS[currentStep - 1];
    const stepErrors = validator(formData);
    setErrors(stepErrors);
    return !hasErrors(stepErrors);
  };

  const goNext = () => {
    if (!validateCurrentStep()) return;
    if (currentStep < totalSteps) {
      setErrors({});
      setCurrentStep((s) => s + 1);
      window.scrollTo({ top: document.getElementById("apply")?.offsetTop ?? 0, behavior: "smooth" });
    }
  };

  const goBack = () => {
    if (currentStep > 1) {
      setErrors({});
      setCurrentStep((s) => s - 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) return;

    setIsSubmitting(true);

    const recaptchaSuccess = await googleRecaptcha(executeRecaptcha);
    if (!recaptchaSuccess) {
      setIsSubmitting(false);
      return;
    }

    // KOL submissions go to a local Strapi instance when configured, since
    // the kol-submission-form-data content-type isn't deployed to staging/prod yet.
    const kolStrapiBase = process.env.NEXT_PUBLIC_LOCAL_STRAPI_API_URL || getStrapiURL();
    const url = `${kolStrapiBase}/api/kol-submission-form-datas`;
    const res = await postRequest(url, {
      formInputs: mapToKolSubmission(formData),
      token: recaptchaSuccess,
    });

    if (res.status === 400) {
      setFormSubmitError(true);
      setIsSubmitting(false);
      setTimeout(() => {
        setFormSubmitError(false);
      }, 1500);
      return;
    }

    const source = window.location.pathname.slice(1);
    const conversionMeta = {
      formType: "kol_form",
      leadType: "creator_application" as const,
      source,
    };

    pushEnhancedConversionsData(
      {
        name: formData.fullName,
        email: formData.email,
        mobileNumber: formData.mobile,
      },
      conversionMeta
    );

    setTimeout(() => {
      setIsSubmitting(false);
      setFormSubmitted(true);
      router.push(getThankYouUrl(conversionMeta, "/kol-koc/thankyou"));
    }, 2000);
  };

  const renderStep = () => {
    const stepProps = { data: formData, onUpdate: update, errors };
    switch (currentStep) {
      case 1:
        return <StepAbout {...stepProps} />;
      case 2:
        return <StepSocials {...stepProps} />;
      case 3:
        return <StepContent {...stepProps} />;
      case 4:
        return <StepPreferences {...stepProps} />;
      case 5:
        return <StepJoin {...stepProps} />;
      default:
        return null;
    }
  };

  return (
    <section id="apply" className="relative bg-darkPurple pt-16 pb-20 md:pb-28">
      <RevealElement className="px-7 sm:px-[10.417vw] mb-12">
        <p className="text-center text-turquoise font-mono uppercase tracking-[0.2em] body-small-fontsize mb-4">
          {content.eyebrow}
        </p>
        <h2 className="text-white text-center">{content.heading}</h2>
        <p className="text-center text-white/70 md:px-20">{content.desc}</p>
      </RevealElement>

      <div className="s m:px-[5.5vw]">
        <div className="bg-white/[0.04] border border-white/10 rounded-3xl p-[30px] md:p-[46px]">
          <ProgressSteps steps={STEP_LABELS} currentStep={currentStep} />

          <form
            noValidate
            onSubmit={(e) => e.preventDefault()}
            className="relative"
          >
            {renderStep()}
          </form>

          <div className="flex items-center justify-between mt-10">
            <button
              type="button"
              onClick={goBack}
              disabled={currentStep === 1}
              className="rounded-full border border-white/30 text-white px-6 py-3 text-sm font-semibold disabled:opacity-0 disabled:pointer-events-none hover:border-white/60"
            >
              Back
            </button>

            {currentStep < totalSteps ? (
              <button
                type="button"
                onClick={goNext}
                className={`rounded-full bg-green text-darkGrey px-8 py-3 text-sm font-bold hover:opacity-90 ${
                  hasErrors(errors) ? "opacity-40 hover:opacity-40" : ""
                }`}
              >
                Continue
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`rounded-full bg-green text-darkGrey px-8 py-3 text-sm font-bold hover:opacity-90 ${
                  isSubmitting ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                {isSubmitting ? "Submitting…" : "Submit application"}
              </button>
            )}
          </div>
        </div>
      </div>

      <div
        className={`absolute bottom-0 left-0 right-0 z-10 flex my-4 m-auto w-fit h-fit items-center gap-3 backdrop-blur-xl px-6 py-4 rounded-xl bg-gradient-to-b from-[#8067b4] to-[#512e99] mt-6 pointer-events-none ${
          formSubmitted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
        } duration-300`}
      >
        <Image src={NotifiactionIcon} alt={""} width={26} height={26} />
        <span className="text-white text-font-18">
          Thanks for applying! We&apos;ll be in touch.
        </span>
      </div>

      <div
        className={`absolute left-0 bottom-0 right-0 m-auto flex my-4 w-fit h-fit items-center gap-3 backdrop-blur-xl px-6 py-3 rounded-xl bg-gradient-to-b from-[#8067b4] to-[#512e99] mt-6 ${
          formSubmitError ? "opacity-100 translate-y-0" : "opacity-0 translate-y-7"
        } duration-300 z-20 pointer-events-none shadow-red-500 drop-shadow-[0_0_10px_rgba(220,38,38,0.5)]`}
      >
        <span className="text-sm text-white tracking-wider">
          Something went wrong. Please try again.
        </span>
      </div>

      <div
        className={`absolute left-0 bottom-0 right-0 m-auto flex my-4 w-fit h-fit items-center gap-3 backdrop-blur-xl px-6 py-3 rounded-xl bg-gradient-to-b from-[#8067b4] to-[#512e99] mt-6 ${
          isSubmitting ? "opacity-100 translate-y-0" : "opacity-0 translate-y-7"
        } duration-300 z-20 pointer-events-none shadow-red-500 drop-shadow-[0_0_10px_rgba(220,38,38,0.5)]`}
      >
        <span className="text-sm text-white tracking-wider">
          Sending your application now. Thanks for waiting!
        </span>
      </div>
    </section>
  );
}
